const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 180;

const NODE_DIMENSIONS = {
  trigger:                      { w: 280, h: 120 },
  end:                          { w: 200, h:  80 },
  delay:                        { w: 220, h: 100 },
  assignAgent:                  { w: 260, h: 140 },
  transferToFlow:               { w: 260, h: 120 },
  template:                     { w: 320, h: 260 },
  textMessage:                  { w: 300, h: 200 },
  condition:                    { w: 300, h: 220 },
  controller:                   { w: 300, h: 220 },
  interactiveButtons:           { w: 320, h: 280 },
  interactiveList:              { w: 320, h: 260 },
  interactiveUrl:               { w: 300, h: 200 },
  interactiveCarouselUrl:       { w: 340, h: 280 },
  interactiveCarouselButton:    { w: 340, h: 280 },
  webhook:                      { w: 320, h: 260 },
  knowledgeBase:                { w: 300, h: 200 },
  aiAskQuestion:                { w: 300, h: 200 },
  captureText:                  { w: 300, h: 200 },
  aiIntentClassifier:           { w: 320, h: 240 },
  aiEntityExtractor:            { w: 320, h: 240 },
  aiResponseGenerator:          { w: 300, h: 200 },
  aiKnowledgeBaseAnswer:        { w: 380, h: 320 },
};

function getNodeSize(node) {
  const dim = NODE_DIMENSIONS[node.type];
  return dim
    ? { width: dim.w, height: dim.h }
    : { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
}

const H_GAP = 160;
const V_GAP = 80;
const BRANCH_GAP = 40;
const PAD = 50;

/**
 * Hierarchical left-to-right layout with branch separation and multi-parent
 * merge-node handling.
 *
 * Options:
 *   skipNodeIds  – Set<string> of node IDs whose positions should NOT be
 *                  changed (manually-placed nodes). The algorithm still
 *                  computes their levels so other nodes lay out correctly,
 *                  but the final position for skipped nodes is taken from
 *                  their existing `node.position`.
 *
 * Algorithm overview:
 *   1. Trigger node(s) are always level 0 (leftmost column).
 *   2. BFS shortest-path from triggers assigns every reachable node a level
 *      (earliest possible column, keeping the graph compact).
 *   3. Back-edges (cycles) detected via DFS and excluded from level calc.
 *   4. Disconnected nodes land one level past the deepest reachable node.
 *   5. Within each level, nodes are sorted by median parent-center-Y to
 *      reduce edge crossings.
 *   6. Branch separation: when a parent has multiple children in the SAME
 *      level, extra vertical spacing is added per branch to spread them.
 *   7. Multi-parent merge nodes receive a slight X nudge rightward so the
 *      merge edge routing is cleaner.
 *   8. Each column's width = widest node in that level; levels are spaced
 *      by H_GAP.
 *   9. Everything is shifted so the flow starts at (PAD, PAD).
 */
export function getLayoutedElements(nodes, edges, _direction = "LR", options = {}) {
  if (!nodes.length) return { nodes, edges };

  const skipNodeIds = options.skipNodeIds || new Set();

  // ── build graph structures ──
  const nodeMap = {};
  const children = {};
  const parents = {};
  const nodeIds = [];

  nodes.forEach((n) => {
    nodeMap[n.id] = n;
    children[n.id] = [];
    parents[n.id] = [];
    nodeIds.push(n.id);
  });

  edges.forEach((e) => {
    if (children[e.source] && parents[e.target]) {
      children[e.source].push(e.target);
      parents[e.target].push(e.source);
    }
  });

  // ── find start nodes ──
  let startIds = nodes.filter((n) => n.type === "trigger").map((n) => n.id);
  if (!startIds.length) {
    startIds = nodes.filter((n) => parents[n.id].length === 0).map((n) => n.id);
  }
  if (!startIds.length) {
    startIds = [nodes[0].id];
  }

  // ── detect back-edges (cycles) via DFS ──
  const backEdges = new Set();
  const visited = new Set();
  const inStack = new Set();

  function dfs(id) {
    visited.add(id);
    inStack.add(id);
    for (const child of children[id]) {
      if (inStack.has(child)) {
        backEdges.add(`${id}::${child}`);
      } else if (!visited.has(child)) {
        dfs(child);
      }
    }
    inStack.delete(id);
  }

  startIds.forEach((id) => { if (!visited.has(id)) dfs(id); });
  nodeIds.forEach((id) => { if (!visited.has(id)) dfs(id); });

  // ── compute levels (BFS shortest path from starts, ignoring back-edges) ──
  // Each node gets the EARLIEST level it can appear at, keeping the graph
  // compact and preventing loop-adjacent nodes from being pushed far right.
  const level = {};
  nodeIds.forEach((id) => { level[id] = -1; });

  const queue = [];
  startIds.forEach((id) => {
    level[id] = 0;
    queue.push(id);
  });

  let qi = 0;
  while (qi < queue.length) {
    const id = queue[qi++];
    for (const child of children[id]) {
      if (backEdges.has(`${id}::${child}`)) continue;
      if (level[child] < 0) {
        level[child] = level[id] + 1;
        queue.push(child);
      }
    }
  }

  const maxLevel = Math.max(0, ...nodeIds.map((id) => level[id]).filter((l) => l >= 0));
  nodeIds.forEach((id) => {
    if (level[id] < 0) level[id] = maxLevel + 1;
  });

  // ── group by level ──
  const groups = {};
  nodeIds.forEach((id) => {
    const l = level[id];
    if (!groups[l]) groups[l] = [];
    groups[l].push(id);
  });

  const sortedLevels = Object.keys(groups).map(Number).sort((a, b) => a - b);

  // ── sort within each level by median Y of parents (reduce crossings) ──
  const yPos = {};

  for (const l of sortedLevels) {
    const group = groups[l];

    if (l === 0) {
      const origOrder = {};
      nodes.forEach((n, i) => { origOrder[n.id] = i; });
      group.sort((a, b) => origOrder[a] - origOrder[b]);
    } else {
      group.sort((a, b) => {
        const medA = medianParentY(a);
        const medB = medianParentY(b);
        return medA - medB;
      });
    }

    // Branch separation: identify siblings (nodes sharing same parent in
    // this level) and add extra vertical gap between branch groups.
    let curY = 0;
    let prevParentKey = null;
    for (const id of group) {
      const size = getNodeSize(nodeMap[id]);

      const parentKey = parents[id]
        .filter((p) => !backEdges.has(`${p}::${id}`))
        .sort()
        .join(",") || "__root__";

      if (prevParentKey !== null && parentKey !== prevParentKey) {
        curY += BRANCH_GAP;
      }
      prevParentKey = parentKey;

      yPos[id] = curY;
      curY += size.height + V_GAP;
    }
  }

  function medianParentY(id) {
    const pars = parents[id].filter((p) => yPos[p] !== undefined);
    if (!pars.length) return 0;
    const ys = pars.map((p) => yPos[p] + getNodeSize(nodeMap[p]).height / 2).sort((a, b) => a - b);
    const mid = Math.floor(ys.length / 2);
    return ys.length % 2 === 1 ? ys[mid] : (ys[mid - 1] + ys[mid]) / 2;
  }

  // ── center each level vertically around the global midpoint ──
  const globalMaxGroupH = Math.max(
    ...sortedLevels.map((l) => {
      const g = groups[l];
      const last = g[g.length - 1];
      return yPos[last] + getNodeSize(nodeMap[last]).height;
    })
  );

  for (const l of sortedLevels) {
    const g = groups[l];
    const last = g[g.length - 1];
    const groupH = yPos[last] + getNodeSize(nodeMap[last]).height;
    const offset = (globalMaxGroupH - groupH) / 2;
    for (const id of g) {
      yPos[id] += offset;
    }
  }

  // ── compute X per level (column widths) ──
  const levelX = {};
  let curX = 0;
  for (const l of sortedLevels) {
    const maxW = Math.max(...groups[l].map((id) => getNodeSize(nodeMap[id]).width));
    levelX[l] = curX;
    curX += maxW + H_GAP;
  }

  // ── multi-parent merge nudge: shift merge nodes slightly right ──
  const MERGE_NUDGE_X = 30;

  // ── assemble final positions, shifted to (PAD, PAD) ──
  const positions = {};
  let minY = Infinity;
  nodeIds.forEach((id) => {
    if (yPos[id] < minY) minY = yPos[id];
  });

  const yShift = PAD - minY;
  nodeIds.forEach((id) => {
    const mergeOffset = parents[id].length > 1 ? MERGE_NUDGE_X : 0;
    positions[id] = {
      x: PAD + levelX[level[id]] + mergeOffset,
      y: yPos[id] + yShift,
    };
  });

  const layoutedNodes = nodes.map((node) => {
    if (skipNodeIds.has(node.id)) {
      return node;
    }
    return {
      ...node,
      position: positions[node.id] || { x: 0, y: 0 },
    };
  });

  return { nodes: layoutedNodes, edges };
}

/**
 * Compute the set of node IDs downstream of `changedIds` (inclusive),
 * limited to `maxDepth` hops.  Used by selective re-layout to decide
 * which nodes should be repositioned.
 */
export function getImpactedSubgraph(nodes, edges, changedIds, maxDepth = 3) {
  const children = {};
  nodes.forEach((n) => { children[n.id] = []; });
  edges.forEach((e) => {
    if (children[e.source]) children[e.source].push(e.target);
  });

  const impacted = new Set(changedIds);
  let frontier = [...changedIds];

  for (let d = 0; d < maxDepth && frontier.length; d++) {
    const next = [];
    for (const id of frontier) {
      for (const child of (children[id] || [])) {
        if (!impacted.has(child)) {
          impacted.add(child);
          next.push(child);
        }
      }
    }
    frontier = next;
  }

  return impacted;
}
